// Downloadpage.jsx
// URL: /download?token=xxxxx
// Validates the one-time JWT -> shows student info -> opens the certificate

import { useEffect, useState, useRef } from "react";

const API_BASE = process.env.REACT_APP_API_URL || "/api";

const STATUS = {
  LOADING:     "loading",
  READY:       "ready",
  USED:        "used",
  EXPIRED:     "expired",
  INVALID:     "invalid",
  DOWNLOADING: "downloading",
  DONE:        "done",
};

export default function DownloadPage() {
  const [status, setStatus]   = useState(STATUS.LOADING);
  const [student, setStudent] = useState(null);
  const [token, setToken]     = useState(null);
  const [error, setError]     = useState("");
  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return; // StrictMode double-call guard
    calledRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const t = params.get("token");
    if (!t) {
      setStatus(STATUS.INVALID);
      setError("No download token was found in the link.");
      return;
    }
    setToken(t);
    validateToken(t);
  }, []);

  async function validateToken(t) {
    try {
      const res = await fetch(`${API_BASE}/validate-token?token=${encodeURIComponent(t)}`);
      const data = await res.json();

      if (!data.success) {
        if (data.alreadyUsed) setStatus(STATUS.USED);
        else if ((data.message || "").toLowerCase().includes("expire")) setStatus(STATUS.EXPIRED);
        else setStatus(STATUS.INVALID);
        setError(data.message);
      } else {
        setStudent(data.student);
        setStatus(STATUS.READY);
      }
    } catch {
      setStatus(STATUS.INVALID);
      setError("We could not reach the server. Please try again in a moment.");
    }
  }

  async function handleDownload() {
    if (!student || !token) return;

    if (!student.verifyId) {
      setError(
        "This certificate is missing its verification ID, so the QR code would not work. Please contact the administrator."
      );
      return;
    }

    setStatus(STATUS.DOWNLOADING);

    const certParams = new URLSearchParams({
      name:           student.name,
      course:         student.course,
      months:         student.months || "",
      completionDate: student.completionDate || "",
      verifyId:       student.verifyId,
    });

    // Mark the token as used, then open the certificate.
    // If marking fails, still let the student through — the
    // "My Certificate" page is always available as a fallback.
    try {
      await fetch(`${API_BASE}/mark-token-used`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
    } catch {
      /* non-blocking */
    }

    window.location.href = `/certificate?${certParams.toString()}`;
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div style={styles.logo}>🎓 Uncodemy</div>
        <p style={styles.tagline}>Certificate Download</p>
      </div>

      <div style={styles.card}>

        {status === STATUS.LOADING && (
          <CenterState
            icon="⏳"
            title="Verifying your link..."
            subtitle="This will only take a moment."
          />
        )}

        {status === STATUS.READY && student && (
          <div style={styles.section}>
            <div style={styles.readyBadge}>✅ Link is Valid</div>

            <h2 style={styles.title}>Your Certificate is Ready</h2>

            <div style={styles.infoCard}>
              <InfoRow label="Name"   value={student.name} />
              <InfoRow label="Course" value={student.course} />
              {student.months && (
                <InfoRow label="Duration" value={`${student.months} months`} />
              )}
              {student.completionDate && (
                <InfoRow label="Completion Date" value={formatDate(student.completionDate)} />
              )}
            </div>

            {error && <div style={styles.errorBox}>{error}</div>}

            <div style={styles.infoBox}>
              ℹ️ <strong>Good to know:</strong> this email link works once, but you can
              always download your certificate again from the{" "}
              <a href="/my-certificate" style={{ color: "#ff5421", fontWeight: 700 }}>
                My Certificate
              </a>{" "}
              page using your email address.
            </div>

            <button style={styles.btnDownload} onClick={handleDownload}>
              📥 Open Certificate
            </button>
          </div>
        )}

        {status === STATUS.DOWNLOADING && (
          <CenterState
            icon="⬇️"
            title="Preparing your certificate..."
            subtitle="Please do not close this page."
          />
        )}

        {status === STATUS.DONE && (
          <CenterState
            icon="🎉"
            title="Download Started"
            subtitle="Your certificate is being saved. Congratulations!"
            color="#059669"
          />
        )}

        {status === STATUS.USED && (
          <ErrorState
            icon="🔒"
            title="Link Already Used"
            message={
              error ||
              "This download link has already been used."
            }
          />
        )}

        {status === STATUS.EXPIRED && (
          <ErrorState
            icon="⏰"
            title="Link Expired"
            message="This download link is no longer valid."
          />
        )}

        {status === STATUS.INVALID && (
          <ErrorState
            icon="❌"
            title="Invalid Link"
            message={error || "This link is not valid. Please use the link sent to your email."}
          />
        )}
      </div>

      <p style={styles.footer}>
        © Uncodemy Edutech Pvt. Ltd. · Need help? Contact the administrator.
      </p>
    </div>
  );
}

/* ── Sub-components ─────────────────────────────────── */

function CenterState({ icon, title, subtitle, color = "#ff5421" }) {
  return (
    <div style={{ textAlign: "center", padding: "24px 0" }}>
      <div style={{ fontSize: 52, marginBottom: 12 }}>{icon}</div>
      <h2 style={{ ...styles.title, color, textAlign: "center" }}>{title}</h2>
      <p style={styles.subtitle}>{subtitle}</p>
    </div>
  );
}

function ErrorState({ icon, title, message }) {
  return (
    <div style={{ textAlign: "center", padding: "24px 0" }}>
      <div style={{ fontSize: 52, marginBottom: 12 }}>{icon}</div>
      <h2 style={{ ...styles.title, color: "#dc2626", textAlign: "center" }}>{title}</h2>
      <div style={styles.errorBox}>{message}</div>

      {/* Every dead end has a way forward */}
      <a href="/my-certificate" style={styles.btnFallback}>
        📥 Download From My Certificate Page
      </a>

      <p style={{ ...styles.subtitle, marginTop: 16 }}>
        Still stuck?{" "}
        <a href="mailto:admin@uncodemy.com" style={{ color: "#ff5421" }}>
          admin@uncodemy.com
        </a>
      </p>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={styles.infoRow}>
      <span style={styles.infoLabel}>{label}</span>
      <span style={styles.infoValue}>{value || "—"}</span>
    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const s = String(dateStr).trim();
  const dmy = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/);
  const d = dmy ? new Date(+dmy[3], +dmy[2] - 1, +dmy[1]) : new Date(s);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

/* ── Styles ─────────────────────────────────────────── */

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #f0f4ff 0%, #faf5ff 100%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "40px 16px",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
  },
  header: { textAlign: "center", marginBottom: 32 },
  logo: { fontSize: 28, fontWeight: 700, color: "#ff5421" },
  tagline: { color: "#64748b", fontSize: 14, marginTop: 4 },
  card: {
    background: "#fff",
    borderRadius: 16,
    boxShadow: "0 4px 24px rgba(79,70,229,0.10)",
    padding: "36px 40px",
    width: "100%",
    maxWidth: 480,
    boxSizing: "border-box",
  },
  section: { display: "flex", flexDirection: "column" },
  readyBadge: {
    background: "#d1fae5", color: "#065f46",
    fontSize: 13, fontWeight: 600,
    padding: "6px 14px", borderRadius: 20,
    display: "inline-block", marginBottom: 14, alignSelf: "flex-start",
  },
  title: { fontSize: 22, fontWeight: 700, color: "#1e1b4b", margin: "0 0 16px" },
  subtitle: { color: "#64748b", fontSize: 14, lineHeight: 1.6 },
  infoCard: {
    background: "#f8fafc", borderRadius: 10,
    padding: "16px 18px", border: "1px solid #e2e8f0", marginBottom: 16,
  },
  infoRow: {
    display: "flex", justifyContent: "space-between",
    padding: "7px 0", borderBottom: "1px solid #f1f5f9", gap: 12,
  },
  infoLabel: { fontSize: 13, color: "#64748b", fontWeight: 500 },
  infoValue: { fontSize: 14, color: "#1e1b4b", fontWeight: 600, textAlign: "right" },
  infoBox: {
    background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1e40af",
    borderRadius: 8, padding: "12px 16px", fontSize: 13,
    marginBottom: 20, lineHeight: 1.7,
  },
  btnDownload: {
    width: "100%", padding: "14px",
    background: "linear-gradient(135deg, #ff5421, #7c3aed)",
    color: "#fff", border: "none", borderRadius: 10,
    fontSize: 16, fontWeight: 700, cursor: "pointer",
    boxShadow: "0 4px 14px rgba(79,70,229,0.35)",
    fontFamily: "inherit",
  },
  btnFallback: {
    display: "block", width: "100%", boxSizing: "border-box",
    padding: "13px", marginTop: 18,
    background: "#ff5421", color: "#fff",
    borderRadius: 10, textDecoration: "none",
    fontSize: 15, fontWeight: 700, textAlign: "center",
  },
  errorBox: {
    background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626",
    borderRadius: 8, padding: "12px 16px", fontSize: 14,
    marginTop: 8, marginBottom: 8, lineHeight: 1.6,
  },
  footer: { color: "#94a3b8", fontSize: 12, marginTop: 24, textAlign: "center" },
};